// Build the store's initial value tree. Start from the definition's defaults
// (createDefaultValue) and overlay a host-provided `defaultValue`, walking the
// field tree so groups recurse and repeatable-group rows are normalized with a
// fresh internal row key. Leaf values from `defaultValue` win over schema
// defaults.

import {
  isGroupField,
  isRepeatableGroup,
  type DynamicFormDefinition,
  type DynamicFormField,
  type DynamicFormValue,
} from './types'
import { asObject, createDefaultValue, newGroupRow } from './value'

export function seedValue(
  definition: DynamicFormDefinition,
  provided: DynamicFormValue | undefined,
): DynamicFormValue {
  // `base` is the complete default tree for the whole form. When there's no
  // host-provided value there's nothing to overlay, so the defaults are the
  // final answer and we can skip the walk entirely.
  const base = createDefaultValue(definition)
  if (provided == null) return base
  // Otherwise overlay `provided` onto `base`, descending the field tree.
  return mergeFields(definition.fields, base, provided)
}

// mergeFields overlays one level of `provided` onto one level of `base`, then
// recurses into nested groups. The recursion always moves in lockstep down
// three parallel structures:
//
//   fields   — the schema for THIS level (what keys exist, which are groups)
//   base     — the default values for THIS level (always fully populated)
//   provided — the host's values for THIS level (may be partial or missing keys)
//
// Each call returns the merged object for a single level. Group fields hand
// their children's slice of all three structures to a nested call, so every
// recursion step shrinks the scope to one subtree. Leaves are the base case:
// they just copy the incoming value and don't recurse.
function mergeFields(
  fields: DynamicFormField[],
  base: DynamicFormValue,
  provided: Record<string, unknown>,
): DynamicFormValue {
  // Start from the defaults so any field the host DIDN'T mention keeps its
  // default. We only mutate keys that appear in `provided` below.
  const result: DynamicFormValue = { ...base }
  for (const field of fields) {
    if (!field.key) continue
    // Host said nothing about this field — leave the default in place.
    if (!(field.key in provided)) continue
    const incoming = provided[field.key]

    if (isGroupField(field)) {
      const children = field.children ?? []
      if (isRepeatableGroup(field)) {
        // A repeatable group is an array of rows. There's no per-row default in
        // `base` to merge against, so each row starts from a fresh default row
        // (newGroupRow gives child defaults + a new internal __key) and the
        // host's row data is overlaid onto it. Recursing per row reuses the
        // exact same merge logic one level deeper.
        const rows = Array.isArray(incoming) ? incoming : []
        result[field.key] = rows.map((row) => {
          const seededRow = newGroupRow(field) // child defaults + fresh __key
          return mergeFields(children, seededRow, asObject(row))
        })
      } else {
        // A plain group is a nested object. Recurse with the children's schema,
        // the group's default subtree (base[field.key]), and the host's subtree
        // (incoming) so the same default-vs-override rules apply one level down.
        result[field.key] = mergeFields(children, asObject(base[field.key]), asObject(incoming))
      }
      continue
    }

    // Leaf field: the host value wins outright over the schema default.
    result[field.key] = coerceToFieldType(field, incoming)
  }
  return result
}

// A config rule's Basic Value is stored as text, so a boolean it answers arrives
// as "true". Alpaca ticked for either form (alpaca-1.5.29.js:28538); the
// renderer's `value === true` does not.
function coerceToFieldType(field: DynamicFormField, value: unknown): unknown {
  if (field.type !== 'boolean' || typeof value !== 'string') return value
  if (value === 'true') return true
  if (value === 'false') return false
  return value
}
